<template>
  <div data-testid="savedSearchesList" class="space-y-2">
    <template v-if="!isLoading">
      <div
        v-for="savedSearch in savedSearches"
        :key="savedSearch.id"
        class="relative">
        <SavedSearchesListItem
          :saved-search-item="savedSearch"
          @more-menu-closed="emit('savedSearchEditMenuClosed')"
          @more-menu-opened="emit('savedSearchEditMenuOpened')"
          @saved-search-clicked="emit('savedSearchClicked')"
          @search-deleted="handleDeletedSearch"
          @search-edited="handleSearchEdited" />
      </div>
    </template>
    <template v-else>
      <UiSkeletonBox
        v-for="i in 5"
        :key="i"
        :dynamic-size="true"
        :min-width="60"
        :max-width="95" />
    </template>

    <Teleport to="#snackbar">
      <DpaNewsSnackbar
        :show-snackbar="showSnackbar"
        :icon="snackbarIcon"
        @hide-snackbar="showSnackbar = false">
        {{ snackbarMessage }}
      </DpaNewsSnackbar>
    </Teleport>
  </div>
</template>
<script setup lang="ts">
import UiSkeletonBox from "@dpa-id-components/dpa-shared-components/src/components/UiSkeletonBox/UiSkeletonBox.vue";
import { storeToRefs } from "pinia";
import { type MaybeRefOrGetter, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import DpaNewsSnackbar from "@/components/shared/DpaNewsSnackbar/DpaNewsSnackbar.vue";
import useSavedSearchesStore from "@/stores/search/savedSearches.ts";

import SavedSearchesListItem from "./SavedSearchesListItem.vue";

const savedSearchesStore = useSavedSearchesStore();
const { savedSearches, savedSearchesCallFinished, savedSearchesCount } =
  storeToRefs(savedSearchesStore);

const emit = defineEmits<{
  savedSearchesLoaded: [savedSearchesCount: MaybeRefOrGetter<number>];
  savedSearchClicked: [];
  savedSearchEditMenuOpened: [];
  savedSearchEditMenuClosed: [];
}>();

(async () => {
  await savedSearchesStore.getSavedSearches();
})();

const { t } = useI18n();

const isLoading = ref(true);

savedSearchesStore.finallyFetchedSavedSearches(() => {
  isLoading.value = false;
  emit("savedSearchesLoaded", savedSearchesStore.savedSearchesCount);
});

watchEffect(() => {
  if (savedSearchesCallFinished.value) {
    isLoading.value = false;
  }

  emit("savedSearchesLoaded", savedSearchesCount.value);
});

const showSnackbar = ref(false);
const snackbarMessage = ref("");
const snackbarIcon = ref();

const handleDeletedSearch = async () => {
  snackbarIcon.value = "delete";
  snackbarMessage.value = t("savedSearches.deleteSuccessful");
  showSnackbar.value = true;
  emit("savedSearchEditMenuClosed");

  await savedSearchesStore.getSavedSearches();
};

const handleSearchEdited = async () => {
  snackbarMessage.value = t("savedSearches.changedSuccessful");
  snackbarIcon.value = "check";
  showSnackbar.value = true;

  emit("savedSearchEditMenuClosed");
  await savedSearchesStore.getSavedSearches();
};
</script>
